import { useState } from "react";
import LogSessionModal from "../components/LogSessionModal";
import { weekStart, weekEnd, fmtWeight, wtLabel } from "../helpers";

export default function DashboardView({ exercises, workouts, onLogSession, units = "lbs" }) {
  const [logging, setLogging] = useState(false);
  const [expandedEx, setExpandedEx] = useState(null);

  const ws = weekStart(), we = weekEnd();

  const weekSets = exercises.flatMap(ex =>
    ex.sets.filter(s => s.date >= ws && s.date <= we).map(s => ({ ...s, muscle: ex.muscle, exName: ex.name }))
  );

  const weekVol       = weekSets.reduce((a, s) => a + s.reps * s.weight, 0);
  const weekBest      = weekSets.reduce((b, s) => s.weight > (b || 0) ? s.weight : b, 0);
  const weekExercises = new Set(weekSets.map(s => s.exName)).size;

  // Group this week's sets by exercise
  const weekByEx = {};
  exercises.forEach(ex => {
    const exWeekSets = ex.sets.filter(s => s.date >= ws && s.date <= we);
    if (exWeekSets.length > 0) weekByEx[ex.name] = { muscle: ex.muscle, sets: exWeekSets };
  });
  const exList = Object.entries(weekByEx);

  // Top 5 all-time peak loads
  const topSets = exercises.map(ex => {
    const best = ex.sets.reduce((b, s) => s.weight > (b?.weight||0) || (s.weight === (b?.weight||0) && s.reps > (b?.reps||0)) ? s : b, null);
    return best ? { exName: ex.name, muscle: ex.muscle, weight: best.weight, reps: best.reps } : null;
  }).filter(Boolean).sort((a, b) => b.weight - a.weight).slice(0, 5);

  const fmt = v => v > 999 ? `${(v/1000).toFixed(1)}K` : String(Math.round(v));

  return (
    <div className="fade-in">

      {/* ── STATS GRID ── */}
      <section style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:28 }}>
        {[
          { label:"WEEK VOLUME",    value: weekVol   ? fmt(weekVol)                               : "0",  unit: wtLabel(units) },
          { label:"WEEK SETS",      value: String(weekSets.length),                                  unit: "sets" },
          { label:"EXERCISES",      value: String(weekExercises),                                    unit: "this week" },
          { label:"BEST WEIGHT",    value: weekBest  ? fmtWeight(weekBest, units)                 : "—",  unit: weekBest ? wtLabel(units) : "" },
        ].map(({ label, value, unit }) => (
          <div key={label} style={{
            background:"var(--bg-low)", padding:"16px 14px", borderRadius:8,
            display:"flex", flexDirection:"column", gap:4,
          }}>
            <span style={{ fontFamily:"'Space Grotesk',sans-serif", color:"#64748b", fontSize:9, letterSpacing:"0.18em", fontWeight:700, textTransform:"uppercase" }}>{label}</span>
            <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:28, color:"#b9c8de", letterSpacing:"-0.03em", lineHeight:1 }}>{value}</span>
            {unit && <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:9, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.1em" }}>{unit}</span>}
          </div>
        ))}
      </section>

      {/* ── EXERCISES THIS WEEK ── */}
      <section style={{ marginBottom:28 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <h3 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:12, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--text)" }}>THIS WEEK</h3>
          <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:10, color:"#64748b", letterSpacing:"0.1em" }}>{weekSets.length} SETS</span>
        </div>
        {exList.length === 0 ? (
          <div style={{ background:"var(--bg-low)", borderRadius:10, padding:"28px 16px", textAlign:"center" }}>
            <p style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:10, color:"#64748b", letterSpacing:"0.15em" }}>NO SESSIONS THIS WEEK</p>
          </div>
        ) : (
          <div style={{ background:"var(--bg-low)", borderRadius:10, overflow:"hidden" }}>
            {exList.map(([name, d], i) => {
              const isOpen = expandedEx === name;
              const vol    = d.sets.reduce((a, s) => a + s.reps * s.weight, 0);
              const best   = d.sets.reduce((b, s) => s.weight > (b?.weight||0) ? s : b, null);
              return (
                <div key={name} style={{ borderBottom: i < exList.length-1 ? "1px solid rgba(68,71,76,0.1)" : "none" }}>
                  <div onClick={() => setExpandedEx(isOpen ? null : name)}
                    style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px", cursor:"pointer" }}
                  >
                    <div>
                      <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:12, fontWeight:700, textTransform:"uppercase" }}>{name}</div>
                      <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:10, color:"#64748b", marginTop:2 }}>{d.muscle} · {d.sets.length} sets</div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:12, fontWeight:700, color:"#b9c8de" }}>{fmtWeight(best?.weight, units)} {wtLabel(units)}</div>
                        <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:10, color:"#64748b" }}>{fmt(vol)} vol</div>
                      </div>
                      <span style={{ color:"#64748b", fontSize:12, transition:"transform 0.2s", display:"inline-block", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{ background:"var(--bg-highest)", padding:"8px 16px 12px" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:4, marginBottom:6 }}>
                        {["DATE","REPS","WEIGHT","DIFF"].map(h => (
                          <span key={h} style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:9, color:"#64748b", fontWeight:700, letterSpacing:"0.1em" }}>{h}</span>
                        ))}
                      </div>
                      {d.sets.map((s, si) => (
                        <div key={si} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:4, padding:"5px 0", borderTop:"1px solid rgba(68,71,76,0.1)" }}>
                          <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:11, color:"#94a3b8" }}>{s.date?.slice(5)}</span>
                          <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:11, color:"var(--text)", fontWeight:700 }}>{s.reps}</span>
                          <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:11, color:"var(--text)", fontWeight:700 }}>{fmtWeight(s.weight, units)}</span>
                          <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:10, color: s.difficulty==="High" ? "var(--error)" : s.difficulty==="Low" ? "var(--teal)" : "#e8a030" }}>{s.difficulty}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── PEAK LOAD REGISTRY ── */}
      <section style={{ marginBottom:28 }}>
        <h3 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:12, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:14 }}>PEAK LOADS</h3>
        <div style={{ background:"var(--bg-low)", borderRadius:10, overflow:"hidden" }}>
          {topSets.length === 0 ? (
            <div style={{ textAlign:"center", padding:"32px 16px" }}>
              <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:10, color:"#64748b", letterSpacing:"0.15em" }}>NO DATA YET — LOG A SESSION</span>
            </div>
          ) : topSets.map((s, i) => (
            <div key={i} style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"12px 16px",
              borderBottom: i < topSets.length-1 ? "1px solid rgba(68,71,76,0.1)" : "none",
            }}>
              <div>
                <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:12, fontWeight:700, textTransform:"uppercase" }}>{s.exName}</div>
                <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:10, color:"#64748b", marginTop:1 }}>{s.muscle}</div>
              </div>
              <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:14, fontWeight:700, color:"#b9c8de" }}>
                {fmtWeight(s.weight, units)} <span style={{ fontSize:9, opacity:0.6 }}>{wtLabel(units)}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── LOG SESSION BOOKMARK TAB ── sits flush above nav top ── */}
      <button onClick={() => setLogging(true)} style={{
        position: "fixed",
        bottom: 104,       /* nav bottom (40) + nav height (~64) = sits at nav top */
        right: 36,
        width: 48,
        height: 56,
        borderRadius: "12px 12px 0 0",
        background: "var(--accent)",
        boxShadow: "0 -2px 12px rgba(0,0,0,0.3)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 3,
        border: "none", cursor: "pointer", zIndex: 49,
        transition: "transform 0.15s",
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        <span style={{ fontSize:7, fontWeight:900, color:"#111", letterSpacing:"0.05em", textTransform:"uppercase" }}>LOG</span>
      </button>

      {logging && (
        <LogSessionModal
          userExercises={exercises}
          userWorkouts={workouts}
          units={units}
          onClose={() => setLogging(false)}
          onSave={session => { onLogSession(session); setLogging(false); }}
        />
      )}
    </div>
  );
}
